/* eslint-disable import/extensions */
import { DeleteFilled, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { Button, Modal, Spin } from 'antd';
import { useEffect, useState, useRef } from 'react';
import TypeAndQueryCard from '../TypeAndQueryCard';
import WhenConditionCard from '../whenConditionCard';
import SQLQueryBuilderCustomUtils from '../custom_utils/SQLQueryBuilderCustomUtils';
import QueryBuilderCustomUtils from '../custom_utils/QueryBuilderCustomUtils';
import loadConfig from "../config";
import { EditButton } from '@refinedev/antd';

const SQLMode = ({ value, fields, onSave, handleOnQueryChange, showButtons, showEditor, handleEditMode, editFormula }) => {
  const [whenData, setWhenData] = useState<any>({
    caseList: value?.caseList || [{ when: '', then: '' }],
  });
  const [elseObj, setElseObj] = useState<any>({
    type: value?.elseType,
    value: value?.elseValue,
    query: value?.elseQuery,
    error: value?.error
  });
  const [allowUpdate, setAllowUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  // Drag and drop state
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [isDragged, setIsDragged] = useState(true)
  const dragHandleRef = useRef<boolean>(false);

  const onTypeSave = (data) => {
    setElseObj(data);
    if (allowUpdate) {
      handleOnQueryChange({
        ...whenData,
        elseType: data.type,
        elseValue: data.value,
        elseQuery: data.query,
        elseError: data.error
      });
    }
  };

  const onSaveHandler = () => {
    onSave({
      sql: SQLQueryBuilderCustomUtils.sqlFormatter(whenData, elseObj),
      json: {
        ...whenData,
        elseType: elseObj.type,
        elseValue: elseObj.value,
        elseQuery: elseObj.query,
        elseError: elseObj.error
      },
    });
  };

  const onWhenSave = (data, index) => {
    const updatedWhenData = {
      ...whenData,
      caseList: whenData.caseList.map((item, i) => (i === index ? data : item)),
    };
    setWhenData(updatedWhenData);

    if (allowUpdate) {
      handleOnQueryChange({
        ...updatedWhenData,
        elseType: elseObj.type,
        elseValue: elseObj.value,
        elseQuery: elseObj.query,
        elseError: elseObj.error
      });
    }
  };

  const addWhenConditionHandler = () => {
    const newWhenData = {
      ...whenData,
      caseList: [...whenData.caseList, { when: '', then: '' }],
    };
    setWhenData(newWhenData);

    if (allowUpdate) {
      handleOnQueryChange({
        ...newWhenData,
        elseType: elseObj.type,
        elseValue: elseObj.value,
        elseQuery: elseObj.query,
        elseError: elseObj.error
      });
    }
  };

  const onDeleteHandler = (index) => {
    const finalData = {
      ...whenData,
      caseList: whenData.caseList.filter((_, i) => i !== index),
    };
    setWhenData(finalData);
    setLoading(true);

    if (allowUpdate) {
      handleOnQueryChange({
        ...finalData,
        elseType: elseObj.type,
        elseValue: elseObj.value,
        elseQuery: elseObj.query,
        elseError: elseObj.error
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Only allow drag if it started from the drag handle
    if (!dragHandleRef.current) {
      e.preventDefault();
      return;
    }

    setDraggingId(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    // Add ghost image
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.opacity = '0.5';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
    dragHandleRef.current = false;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggingId === null || draggingId === index) {
      return;
    }

    e.dataTransfer.dropEffect = 'move';

    if (dragOverId !== index) {
      setDragOverId(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    // Only remove drag-over if we're actually leaving the element
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverId(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggingId === null || draggingId === dropIndex) {
      setDragOverId(null);
      setDraggingId(null);
      return;
    }

    // Reorder the array using filter
    const draggedItem = whenData.caseList[draggingId];
    const filteredList = whenData.caseList.filter((_, index) => index !== draggingId);

    // Adjust drop index when dragging from left to right
    const adjustedDropIndex = draggingId < dropIndex ? dropIndex - 1 : dropIndex;

    const newCaseList = [
      ...filteredList.slice(0, adjustedDropIndex),
      draggedItem,
      ...filteredList.slice(adjustedDropIndex)
    ];

    const updatedWhenData = {
      ...whenData,
      caseList: newCaseList,
    };

    // Update local state
    setWhenData(updatedWhenData);
    setDraggingId(null);
    setDragOverId(null);

    // Notify parent
    if (allowUpdate) {
      handleOnQueryChange({
        ...updatedWhenData,
        elseType: elseObj.type,
        elseValue: elseObj.value,
        elseQuery: elseObj.query,
        elseError: elseObj.error
      });
    }
    setIsDragged(!isDragged)
  };
  const handleMouseDown = () => {
    dragHandleRef.current = true;
  };

  const handleMouseUp = () => {
    dragHandleRef.current = false;
  };

  useEffect(() => {
    if (loading) setLoading(false);
  }, [loading]);

  useEffect(() => {
    if (value) {
      const finalWhenData = { ...value };
      if (!value?.caseList || value.caseList.length === 0) {
        finalWhenData.caseList = [{ when: '', then: '' }];
      }
      setWhenData(finalWhenData);
      setElseObj({
        type: value.elseType,
        value: value.elseValue,
        query: value.elseQuery,
        error: value.error
      });
      setAllowUpdate(true);
    }
  }, [value]);



  const showModal = () => {
    Modal.info({
      title: '',
      width: 1000,
      maskClosable: false,
      content: (
        <div>
          <pre>{SQLQueryBuilderCustomUtils.sqlFormatter(whenData, elseObj)}</pre>
        </div>
      ),
      onOk() { },
    });
  };

  const showJsonLogicModal = () => {
    const loadedConfig = loadConfig('antd', fields);
    let jsonLogic = QueryBuilderCustomUtils.getJsonLogic({
      ...whenData,
      elseType: elseObj.type,
      elseValue: elseObj.value,
      elseQuery: elseObj.query,
      elseError: elseObj.error
    }, loadedConfig);
    Modal.info({
      title: '',
      width: 1000,
      maskClosable: false,
      content: (
        <div>
          <pre>{JSON.stringify(jsonLogic, null, 2)}</pre>
        </div>
      ),
      onOk() { },
    });
  };

  console.log(whenData.caseList, 'whenDataCaseList')

  return (
    <>
      <div>
        {showButtons && (
          <div className="flex justify-end mulitple-option-query-btn-group query-btn-group">
            <Button size='middle' className="mx-2" type="default" key="save" onClick={onSaveHandler}>
              Validate
            </Button>
            <Button size='middle' type='default' className="mx-2" onClick={showModal}>
              Show Query
            </Button>
            <Button size='middle' className='ml-2' type='default' onClick={showJsonLogicModal}>
              Show JSON Logic
            </Button>
          </div>
        )}
      </div>

      {(showEditor === undefined || showEditor) ? (
        <div key={JSON.stringify(isDragged)} className="multiple-option-query-builder">
          {whenData.caseList.map((caseItem, index) => (
            <div
              className={`draggable-item ${draggingId === index ? 'dragging' : ''} ${dragOverId === index ? 'drag-over' : ''}`}
              key={`case-${index}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              {loading ? (
                <Spin />
              ) : (
                <div className="main-container" style={{ position: 'relative' }}>
                  {/* Drag Handle */}
                  <div
                    className="drag-handle"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '12px',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                    }}
                  >
                    <HolderOutlined style={{ fontSize: '18px' }} />
                  </div>

                  {/* Delete Button */}
                  {whenData.caseList.length > 1 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 10,
                      }}
                    >
                      <Button
                        size="small"
                        type="text"
                        style={{ color: 'red' }}
                        icon={<DeleteFilled />}
                        onClick={() => onDeleteHandler(index)}
                      />
                    </span>
                  )}

                  {/* Content */}
                  <div
                    className="when-section-container"
                    style={{
                      marginLeft: '30px',
                      marginRight: '30px'
                    }}
                  >
                    <WhenConditionCard
                      fields={fields}
                      onSave={(data) => onWhenSave(data, index)}
                      value={caseItem}
                      index={index}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          <Button
            size='middle'
            type="default"
            className="mx-2 ml-20 mutiple-title line-val"
            onClick={addWhenConditionHandler}
            
          >
            ++ ELSE IF
          </Button>

          <div
            className="main-container half-height"
            style={{
              padding: '12px',
            
              borderRadius: '6px',
              
            }}
          >
            <TypeAndQueryCard
              title="ELSE"
              onSave={onTypeSave}
              data={elseObj}
              fields={fields}
            />
          </div>
        </div>
      ) : (
        <div className="flex item-center">
          <div className='mr-2'>
            <span className='font-bold'>Formula :  </span>
          </div>
          <pre className='mb-0'>{SQLQueryBuilderCustomUtils.sqlFormatter(whenData, elseObj)}</pre>
          {editFormula && handleEditMode && (
            <EditButton className='border-0' hideText onClick={() => handleEditMode()} />
          )}
        </div>
      )}
    </>
  );
};

export default SQLMode;